<template>
  <div
    class="rounded-lg border-2 border-dashed border-obsidian-300 p-4 text-center"
    @dragover.prevent
    @drop.prevent="handleDrop"
    @click="openFileDialog"
  >
    <input
      ref="fileInput"
      type="file"
      :accept="accept"
      multiple
      class="hidden"
      @change="handleChange"
    />

    <div v-if="filesInternal.length" class="grid grid-cols-3 gap-3 md:grid-cols-4">
      <div
        v-for="(photo, index) in filesInternal"
        :key="photo.url"
        class="group relative overflow-hidden rounded-md border"
      >
        <img
          v-if="isImageFile(photo) && !failedThumbnails.has(photo.url)"
          :src="photo.url"
          :alt="photo.name"
          class="h-24 w-full object-cover"
          @error="failedThumbnails.add(photo.url)"
        />
        <div
          v-else-if="isPdfFile(photo)"
          class="flex h-24 w-full items-center justify-center bg-obsidian-50 text-obsidian-700"
        >
          <div class="flex items-center gap-2 text-sm font-medium">
            <FileTextIcon class="h-5 w-5 text-bittersweet-600" />
            PDF
          </div>
        </div>
        <div
          v-else
          class="flex h-24 w-full items-center justify-center bg-obsidian-50 text-obsidian-700"
        >
          <FileIcon class="h-5 w-5" />
        </div>
        <button
          type="button"
          :aria-label="removeLabel || 'Remove'"
          title=""
          class="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-obsidian-700 shadow hover:bg-obsidian-50"
          @click.stop="remove(index)"
        >
          <Trash2Icon class="h-3.5 w-3.5" />
        </button>
        <div class="pointer-events-none absolute bottom-1 left-1 max-w-[85%]">
          <span
            class="inline-block truncate rounded-full bg-black/20 px-2 py-1 text-xs leading-none text-white backdrop-blur-sm"
            :title="photo.name"
          >
            {{ photo.name }}
          </span>
        </div>
      </div>
      <div
        class="flex cursor-pointer flex-col items-center justify-center rounded-md border border-solid border-obsidian-100 text-xs text-obsidian-600 hover:bg-obsidian-100"
        @click.stop="openFileDialog"
      >
        <UploadIcon class="h-4 w-4 text-obsidian-400" />
        <span>{{ addMoreHint }}</span>
      </div>
    </div>

    <div
      v-else
      class="flex flex-col items-center justify-center gap-2 py-4 text-sm text-obsidian-600"
    >
      <UploadIcon class="h-8 w-8 text-obsidian-400" />
      <div>{{ emptyHint }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, reactive, ref, watch } from 'vue';
import { FileIcon, FileTextIcon, UploadIcon, Trash2Icon } from 'lucide-vue-next';

export interface PreviewFile {
  name: string;
  url: string;
  mimeType?: string;
  /** The picked file, for entries added through the dropzone. */
  file?: File;
}

const props = withDefaults(
  defineProps<{
    files: PreviewFile[];
    emptyHint: string;
    addMoreHint: string;
    removeLabel?: string;
    accept?: string;
  }>(),
  {
    accept: 'image/*',
  },
);

const emit = defineEmits<{
  (e: 'update:files', value: PreviewFile[]): void;
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const filesInternal = ref<PreviewFile[]>(props.files ?? []);
const failedThumbnails = reactive(new Set<string>());

watch(
  () => props.files,
  (val) => {
    filesInternal.value = val ?? [];
  },
  { deep: true },
);

const openFileDialog = () => fileInput.value?.click();

const getFileExtension = (fileName: string) => fileName.split('.').pop()?.toLowerCase() ?? '';

const isImageFile = (file: PreviewFile) => {
  if (file.mimeType) return file.mimeType.startsWith('image/');
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'heics'].includes(
    getFileExtension(file.name),
  );
};

const isPdfFile = (file: PreviewFile) => {
  if (file.mimeType) return file.mimeType === 'application/pdf';
  return getFileExtension(file.name) === 'pdf';
};

const appendFiles = (list: FileList | null | undefined) => {
  if (!list) return;
  const added: PreviewFile[] = Array.from(list).map((file) => ({
    name: file.name,
    url: URL.createObjectURL(file),
    mimeType: file.type,
    file,
  }));
  const next = filesInternal.value.concat(added);
  filesInternal.value = next;
  emit('update:files', next);
};

const handleChange = (evt: Event) => {
  const files = (evt.target as HTMLInputElement).files ?? undefined;
  appendFiles(files);
  if (evt.target) (evt.target as HTMLInputElement).value = '';
};

const handleDrop = (evt: DragEvent) => {
  appendFiles(evt.dataTransfer?.files);
};

const remove = (index: number) => {
  const file = filesInternal.value[index];
  if (file && file.url.startsWith('blob:')) URL.revokeObjectURL(file.url);
  if (file) failedThumbnails.delete(file.url);
  const next = filesInternal.value.filter((_, i) => i !== index);
  filesInternal.value = next;
  emit('update:files', next);
};

onBeforeUnmount(() => {
  for (const file of filesInternal.value) {
    if (file.url.startsWith('blob:')) URL.revokeObjectURL(file.url);
  }
});
</script>
